/**
 * @fileoverview FRE-1313 — the About column: the event's own body copy.
 *
 * ## Why this module did not exist until now
 *
 * `description` has ridden on every event payload since API-1403 (it is an
 * `Event` field and `EventDetailV2` inherits the whole schema), and every
 * lexicon entry has declared an `aboutHeading` — 'About this show', 'About
 * this exhibition', … — since WEB-703. Both halves were there; nothing joined
 * them. `EventSpineTemplate` renders only what `resolveEventModules` returns,
 * that selector only walks `moduleOrder`, and no `moduleOrder` listed an About
 * entry because no `EventModuleKind` named one. So the copy was serialized
 * into the RSC payload on every request and then dropped: on
 * `clt.loci.city/event/…` the body text appears exactly once in the HTML,
 * inside a `<script>`, and nowhere in the rendered markup.
 *
 * That is the third complaint in FRE-1313 ("Event info STILL doesn't show for
 * eventbrite events"). It is not an ingestion gap — the reported event carries
 * a 2,439-char description — which is why the PIP-238/FRE-1313 pipeline work
 * could not have fixed it.
 *
 * ## Plain text, deliberately
 *
 * The body is rendered as text in `<p>` elements, split on blank lines. It is
 * NOT passed through `dangerouslySetInnerHTML`: this copy is scraped from
 * third-party source pages, so treating it as markup would make one of those
 * pages an XSS vector. Any future rich-text
 * support has to arrive as sanitized, structured data on the wire, not by
 * trusting the string here.
 */

import type { ReactElement } from 'react';

import { EventModuleSection } from '@/components/organisms/EventModuleSection';
import { resolveAboutColumnBody } from '@/lib/eventDetail/eventSourceCopy';

import type { EventModuleProps } from './registry';

/**
 * Splits body copy into paragraph blocks on blank lines, dropping empties.
 *
 * Eventbrite's `/e/` extraction joins its structured blocks with `\n\n`
 * (PIP-238), so blank-line separation is the shape this copy actually
 * arrives in. A body with no blank line yields exactly one paragraph rather
 * than collapsing to nothing.
 *
 * Exported for the unit test — the paragraph rule is the part worth asserting
 * independently of React.
 */
export function splitAboutParagraphs(description: string): readonly string[] {
  return description
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
}

/**
 * `AboutModule` — the event's body copy under the category's About heading.
 *
 * Returns `null` when gated off rather than painting its own degrade note:
 * `createEventModuleRenderer` already renders the bare `EventModuleSection`
 * for a `hasContent: false` module (WEB-719), so a note here would be a
 * second opinion on a decision `resolveEventModules` already made.
 */
export function AboutModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  // Read through the same resolver the gate used, never `event.description`
  // raw: for a Ticketmaster row that field is venue policy that
  // `EventSourceInfoBlocks` renders as "Event Info", and printing it here
  // would both duplicate it and label it as a summary the source never wrote.
  const paragraphs = splitAboutParagraphs(resolveAboutColumnBody(event) ?? '');
  // Defensive only: `hasModuleContent('about', …)` is this same resolver, so
  // this cannot be empty without the two disagreeing.
  if (paragraphs.length === 0) {
    return null;
  }

  return (
    <EventModuleSection module={module} chroma={chroma}>
      <div data-testid="event-about-body" className="flex min-w-0 flex-col gap-4">
        {paragraphs.map((paragraph, index) => (
          <p
            // Index keys are correct here: this list is derived from one
            // string, never reordered, and has no identity of its own.
            key={index}
            className="m-0 min-w-0 wrap-break-word text-(length:--evt-body) leading-[1.65] text-fg-2"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </EventModuleSection>
  );
}
